// libs
import React, { memo, useRef, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

// contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// styles
import { Image, Title, Info, Section, Anchor } from './styles';

// helpers
import { getWidgetParams } from './helpers';
import { generateTrackingUrl } from 'shared/components/AdPlatformWidgets/helpers';
import { getPlaceHolderColor } from 'components/AdPlatformWidgets/helpers';
import { generateSameHostUrl } from 'shared/helpers/utils';

// constants
import { DWEB_MAX_WIDTH } from 'shared/styles/base';
import { IMAGE_ALT } from './constants';
import { MARGIN_RIGHT } from 'shared/components/AdPlatformWidgets/Desktop/constant';
import { ON_LOAD_PLACEHOLDER_COLOR } from 'shared/components/AdPlatformWidgets/constants';

// hooks
import { useTrackingObserver } from 'shared/components/AdPlatformWidgets/ImpressionTracking/trackingHooks';
import { useDwebOnClick } from 'shared/components/AdPlatformWidgets/Desktop/hooks';

// def
import { IWidgetItems } from 'shared/components/AdPlatformWidgets/types';

interface ICardProps {
  widgetItem: IWidgetItems;
  numOfColumns: number;
  inventoryName: string;
  className?: string;
  childCount: number;
  contentAlign: string;
  textColor: string;
}

function Card({
  widgetItem,
  numOfColumns,
  inventoryName,
  childCount,
  className = '',
  contentAlign,
  textColor,
}: ICardProps) {
  const { deviceWidth } = useResponsiveContext();

  const containerWidth = deviceWidth > DWEB_MAX_WIDTH ? DWEB_MAX_WIDTH : deviceWidth;

  const { pinnedProductIds, title, description, trackingData, source, aspectRatio } =
    getWidgetParams(widgetItem);

  const actionUrl = generateTrackingUrl(
    widgetItem.parameters.web_action_url,
    inventoryName,
    widgetItem.positionInParent,
    widgetItem.parameters.tracking_parameters,
    pinnedProductIds
  );

  const placeholderColor = getPlaceHolderColor(childCount);

  const visibleMargin = (numOfColumns - 1) * MARGIN_RIGHT;

  const descriptionStyle = useMemo(() => ({ textAlign: contentAlign }), [contentAlign]);

  const canShowDescription = isEmpty(title) === false || isEmpty(description) === false;

  const widgetRef = useRef<HTMLElement>(null);
  const url = actionUrl ? generateSameHostUrl(actionUrl) : undefined;

  const { onWidgetClick } = useTrackingObserver({ widgetRef, trackingData });
  const { onClick } = useDwebOnClick({ url, onWidgetClick, trackingData });

  const Content = (
    <div>
      <Image
        src={source}
        width={(containerWidth - visibleMargin) / numOfColumns}
        aspectRatio={aspectRatio}
        placeholderColor={placeholderColor}
        alt={IMAGE_ALT}
        onLoadPlaceholderColor={ON_LOAD_PLACEHOLDER_COLOR}
      />

      {canShowDescription && (
        <div style={descriptionStyle}>
          {title && <Title textColor={textColor}>{title}</Title>}
          {description && <Info textColor={textColor}>{description}</Info>}
        </div>
      )}
    </div>
  );

  if (isEmpty(actionUrl)) {
    return (
      <Section ref={widgetRef} numOfColumns={numOfColumns} className={className}>
        {Content}
      </Section>
    );
  }

  return (
    <Anchor
      ref={widgetRef}
      href={url}
      aria-label={IMAGE_ALT}
      onClick={onClick}
      className={className}
      numOfColumns={numOfColumns}
    >
      {Content}
    </Anchor>
  );
}

export default memo(Card);
